import React from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Icon, Text, Heading, Button, TextField, Link } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import { useParams } from "react-router";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { getErrorMessage } from "../../../app/state/selectors";
import { sendNumberOrEmail } from "../../../checkout/state/actions/transferOwnershipActions";
import {
    getHasSubmittedPortinData,
    getHasSubmittedTransferOrder,
    getHasRejectedTransferOrder,
} from "../../../checkout/state/selectors/accountSelectors";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import TEXT from "../../text-consts";
import "./LandingPage.less";

interface EnterNumberOrEmailProps {
    hasRejectedTransferOrder: boolean;
    hasSubmittedPortinData: boolean;
    hasSubmittedTransferOrder: boolean;
    isFetchingData: boolean;
    logicalErrorMessage?: string;
}

const EnterNumberOrEmail: React.FC<EnterNumberOrEmailProps> = ({
    // Redux (data)
    isFetchingData,
    hasSubmittedPortinData,
    hasSubmittedTransferOrder,
    hasRejectedTransferOrder,
    logicalErrorMessage,
}) => {
    const dispatch = useDispatch();
    const { orderGuid = "" } = useParams();
    const [number, setNumber] = React.useState("");
    const toggleLegalLink = false; // KS wants to test if this link is confusing customers or not. Remove toggle after test conslusion

    if (hasSubmittedTransferOrder) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.LANDING_PAGE_FROM_CHECKOUT.CONFIRM_HEADING}
                    </Heading>
                </div>
                <Container size="medium" className="tlo-landing-page__confirmation-container">
                    <span>
                        <Icon className="tlo-landing-page__confirmation-icon" name="check-circle" />
                    </span>
                    <div>
                        <Text mb="100" variant="additional-100">
                            {TEXT.ENTER_NUMBER_OR_EMAIL.ORDER_CONFIRMATION}
                        </Text>
                        <Link variant="standalone" href="https://www.telia.no/">
                            {TEXT.LANDING_PAGE_FROM_CHECKOUT.BACK_TO_FRONTPAGE}
                        </Link>
                    </div>
                </Container>
            </div>
        );
    }

    if (hasSubmittedPortinData) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.ENTER_NUMBER_OR_EMAIL.CONFIRM_HEADING}
                    </Heading>
                </div>
                <Container size="small">
                    <div className="tlo-landing-page__portin-receipt">
                        <Icon
                            className="tlo-landing-page__confirmation-icon"
                            name="check-circle"
                            style={{ width: "80px", height: "80px", color: "#00CC66", marginTop: "6px" }}
                        />
                        <div className="spacer-top spacer-sides">
                            <Text mb="100">{TEXT.ENTER_NUMBER_OR_EMAIL.RECEIPT_PARAGRAPH}</Text>
                            <Link variant="standalone" href="/">
                                {TEXT.ENTER_NUMBER_OR_EMAIL.BACK_TO_FRONTPAGE}
                            </Link>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (logicalErrorMessage) {
        return (
            <div className="tlo-landing-page">
                <div className="tlo-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.ENTER_NUMBER_OR_EMAIL.HEADING}
                    </Heading>
                </div>
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="tlo-landing-page">
            <div className="tlo-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.ENTER_NUMBER_OR_EMAIL.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            <Container size="small">
                <div className="tlo-landing-page__sms-email-container">
                    {hasRejectedTransferOrder && (
                        <Alert kind="warning">
                            <div
                                className="paragraph paragraph--fineprint"
                                dangerouslySetInnerHTML={{ __html: TEXT.ENTER_NUMBER_OR_EMAIL.ORDER_REJECTED }}
                            />
                        </Alert>
                    )}
                    <div className="tlo-landing-page__sms-email-info">
                        <Text mb="100">{TEXT.ENTER_NUMBER_OR_EMAIL.TRANSFER_PARAGRAPH}</Text>
                        <Text
                            mb="100"
                            dangerouslySetInnerHTML={{ __html: TEXT.ENTER_NUMBER_OR_EMAIL.APPROVAL_PARAGRAPH }}
                        />
                    </div>
                    <div className="tlo-landing-page__sms-email-form">
                        <form
                            className="spacer-top"
                            onSubmit={(e) => {
                                e.preventDefault();
                                dispatch(sendNumberOrEmail({ number, orderGuid }));
                            }}
                        >
                            <TextField className="tlo-landing-page__sms-email-textfield">
                                <TextField.Label>Mobilnummer (til nåværende eier):</TextField.Label>
                                <TextField.Input
                                    onChange={(event) => setNumber(event.target.value)}
                                    type="text"
                                    value={number}
                                    data-di-mask
                                />
                            </TextField>
                            <div className="tlo-landing-page__submit">
                                <Button variant="primary" type="submit" className="spacer-top">
                                    Send
                                </Button>
                            </div>
                            {toggleLegalLink && (
                                <div className="tlo-landing-page__owner-link">
                                    <Link
                                        variant="standalone"
                                        href={`${PAGES.TRANSFER_OWNERSHIP.LANDING_PAGE_PORTIN_B2C.URL}${orderGuid}/`}
                                    >
                                        {TEXT.ENTER_NUMBER_OR_EMAIL.IS_LEAGAL_OWNER_LINK}
                                    </Link>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </Container>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    logicalErrorMessage: getErrorMessage(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    hasSubmittedPortinData: getHasSubmittedPortinData(state),
    hasSubmittedTransferOrder: getHasSubmittedTransferOrder(state),
    hasRejectedTransferOrder: getHasRejectedTransferOrder(state),
});

export default connect(mapStateToProps)(EnterNumberOrEmail);
